// The runtime's research findings on a card: the answer, then numbered citations (title link,
// host, and the short quote the answer relies on), and "Save answer" to keep it in the Answer
// library. Web pages are evidence, never instructions; links are http(s) only and open in a new
// tab without a referrer.
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { BookmarkPlus, Check, SearchX } from "lucide-react";
import type { CardBlock } from "@shared/core";
import { saveCardAnswer } from "@web/answers/answerOps";
import { Button } from "@web/ui/Button";
import { FormattedText } from "./FormattedText";
import { EmailLinksContext } from "./links";
import { safeHref } from "./markdownLite";
import { citationHost, researchState } from "./research";

function SaveAnswer({ cardId }: { cardId: string }) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<"idle" | "busy" | "saved">("idle");
  if (state === "saved")
    return (
      <p className="flex items-center gap-1.5 text-sm text-muted">
        <Check aria-hidden className="size-4 text-calm" />
        Saved.
        <Link to="/answers" className="text-accent-text underline underline-offset-2">
          Saved answers
        </Link>
      </p>
    );
  return (
    <Button
      size="sm"
      variant="ghost"
      busy={state === "busy"}
      className="-ml-3 self-start"
      onClick={async () => {
        setState("busy");
        setState((await saveCardAnswer(queryClient, cardId)) ? "saved" : "idle");
      }}
    >
      <BookmarkPlus aria-hidden className="size-4" />
      Save answer
    </Button>
  );
}

export function ResearchBlock({ block, cardId }: { block: CardBlock; cardId?: string }) {
  const state = researchState(block);
  if (!state.available) {
    return (
      <section
        aria-label="Research"
        className="flex items-start gap-3 rounded-lg border border-line bg-bg px-4 py-3"
      >
        <SearchX aria-hidden className="mt-0.5 size-4 shrink-0 text-muted" />
        <div className="min-w-0">
          <p className="mm-kicker mb-0.5">Research</p>
          <p className="font-medium text-ink">Couldn’t research this</p>
          <p className="text-sm text-muted">
            {state.reason ? `${state.reason.replace(/[.\s]+$/, "")}. ` : ""}
            The card still stands on the email alone.
          </p>
        </div>
      </section>
    );
  }
  const citations = block.citations ?? [];
  return (
    <section aria-label="Research">
      <h3 className="mm-kicker mb-1.5">{block.label ?? "Research"}</h3>
      {block.title ? <p className="mb-1 font-medium text-ink">{block.title}</p> : null}
      {/* Web research cites pages, not the email: its links show their host but aren't checked. */}
      <EmailLinksContext.Provider value={null}>
        <FormattedText text={state.answer} className="text-ink-2" />
      </EmailLinksContext.Provider>
      {citations.length ? (
        <ol className="mt-3 flex flex-col gap-2 border-t border-line pt-3" aria-label="Sources">
          {citations.map((c, i) => {
            const href = safeHref(c.url);
            const host = citationHost(c.url);
            return (
              <li key={i} className="flex gap-2 text-sm">
                <span className="w-5 shrink-0 text-right text-faint tabular-nums">{i + 1}.</span>
                <span className="min-w-0 break-words">
                  {href ? (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      referrerPolicy="no-referrer"
                      className="text-accent-text underline underline-offset-2"
                    >
                      {c.title || host}
                    </a>
                  ) : (
                    <span className="text-ink-2">{c.title || host}</span>
                  )}
                  <span className="text-faint"> · {host}</span>
                  {c.citedText ? (
                    <span className="mt-0.5 block text-muted">“{c.citedText}”</span>
                  ) : null}
                </span>
              </li>
            );
          })}
        </ol>
      ) : (
        <p className="mt-2 text-sm text-muted">No sources were cited for this answer.</p>
      )}
      {cardId ? (
        <div className="mt-2 flex">
          <SaveAnswer key={`${cardId}:${state.answer.length}`} cardId={cardId} />
        </div>
      ) : null}
    </section>
  );
}
